import { useState, useEffect } from 'react';
import { supabase, auth } from '@/data/supabase';
import Login from './Login';
import Signup from './Signup';

const LOCAL_MODE_KEY   = 'wo_local_mode';
const ALLOW_LOCAL_MODE = import.meta.env.VITE_ALLOW_LOCAL_MODE === 'true';
const ALLOW_SIGNUP     = import.meta.env.VITE_ALLOW_SIGNUP !== 'false';

/**
 * AuthGate — decides what to render based on auth state.
 *
 * Modes:
 *  - No Supabase configured  → renders children with userId=null (pure local mode)
 *  - Supabase + signed in    → renders children with userId
 *  - Supabase + signed out   → renders Login or Signup
 *  - Supabase + local opt-in → bypass gate, render children with userId=null
 */
export default function AuthGate({ children }) {
  const [session, setSession]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [view, setView]             = useState('login'); // 'login' | 'signup'
  const [localMode, setLocalMode]   = useState(
    () => ALLOW_LOCAL_MODE && typeof sessionStorage !== 'undefined' && sessionStorage.getItem(LOCAL_MODE_KEY) === '1'
  );

  // If no Supabase, we render children immediately
  const noSupabase = !supabase;

  useEffect(() => {
    if (noSupabase) {
      setLoading(false);
      return;
    }
    let mounted = true;

    // Initial session check
    auth.getUser().then((res) => {
      if (!mounted) return;
      // getUser returns { data: { user } } — convert to session-like shape
      const user = res?.data?.user || null;
      setSession(user ? { user } : null);
      setLoading(false);
    }).catch(() => {
      if (!mounted) return;
      setLoading(false);
    });

    // Listen for changes
    const sub = auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      if (sess) {
        // Clear local-mode flag once user signs in
        try { sessionStorage.removeItem(LOCAL_MODE_KEY); } catch {}
        setLocalMode(false);
      }
    });

    return () => {
      mounted = false;
      sub?.data?.subscription?.unsubscribe?.();
    };
  }, [noSupabase]);

  const enterLocalMode = () => {
    if (!ALLOW_LOCAL_MODE) return;
    try { sessionStorage.setItem(LOCAL_MODE_KEY, '1'); } catch {}
    setLocalMode(true);
  };

  if (loading) {
    return (
      <div className="auth-shell">
        <div className="auth-wordmark">
          <div className="wmark">W</div>
          PainterOps
        </div>
        <div style={{ color: 'var(--t3)', fontSize: 13 }}>Loading…</div>
      </div>
    );
  }

  // Local-only mode (no Supabase OR user opted in)
  if (noSupabase || localMode) {
    return children({ userId: null, cloudMode: false });
  }

  // Cloud mode, signed in
  if (session?.user) {
    return children({
      userId: session.user.id,
      email:  session.user.email,
      cloudMode: true,
    });
  }

  // Cloud mode, signed out — show auth screens
  if (view === 'signup' && ALLOW_SIGNUP) {
    return (
      <Signup
        onSwitchToLogin={() => setView('login')}
        onLocalMode={enterLocalMode}
      />
    );
  }
  return (
    <Login
      onSwitchToSignup={ALLOW_SIGNUP ? () => setView('signup') : undefined}
      onLocalMode={enterLocalMode}
    />
  );
}
